import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api, type ApiError, type GetObjectLayoutResult } from '@whinerp/client';
import {
  Button,
  EmptyState,
  ErrorState,
  Icon,
  Num,
  PageHeading,
  Panel,
  SectionLabel,
  Skeleton,
  useToast,
} from '@whinerp/ui';
import { formatCount } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';

/**
 * SC-32 Layout builder.
 *
 * The counter is the point. An administrator arranging forty fields has no way
 * of knowing that Simple mode will show eight of them, and will discover it
 * from a colleague who cannot find the field they were told to fill in. So the
 * count and the consequence are both on screen, live, as fields are added -
 * not in a warning after saving.
 *
 * A long layout is not refused. Forty fields in Advanced is a legitimate
 * choice; forty fields that silently become eight is not a choice at all.
 */

type Layout = GetObjectLayoutResult;

function LayoutBuilderScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const toast = useToast();
  const params = useParams();
  const objectKey = params['obj'] ?? 'contact';

  const [placed, setPlaced] = useState<string[] | null>(null);

  const data = useQuery<Layout, ApiError>({
    queryKey: ['object-layout', objectKey],
    queryFn: () => api.getObjectLayout({ path: { objectKey } }),
  });

  useEffect(() => {
    if (!data.data || placed !== null) return;
    setPlaced(data.data.layout.sections.flatMap((section) => section.fields));
  }, [data.data, placed]);

  const save = useMutation({
    mutationFn: (fields: string[]) =>
      api.saveObjectLayout({
        path: { objectKey },
        body: {
          layout: {
            sections: [{ key: 'details', label: 'Details', columns: 2, fields }],
          },
        },
      }),
    onSuccess: (result) => {
      toast.show({
        tone: result.overBudget > 0 ? 'info' : 'success',
        title: t('state.saved'),
        body:
          result.overBudget > 0
            ? t('setup.layout.overBudget', {
                values: {
                  budget: formatCount(result.budget, format),
                  over: formatCount(result.overBudget, format),
                },
              })
            : undefined,
      });
    },
    onError: (error: ApiError) =>
      toast.show({ tone: 'error', title: t('state.error'), body: error.message }),
  });

  if (data.isError) return <LayoutBuilderError error={data.error} retry={() => void data.refetch()} />;
  if (data.isPending || placed === null) return <LayoutBuilderLoading />;

  const available = data.data.fields.filter((field) => !placed.includes(field.key));
  const overBudget = Math.max(placed.length - data.data.budget, 0);

  return (
    <div data-component="LayoutBuilder" className="flex w-full flex-col gap-4">
      <header className="flex flex-wrap items-center gap-3">
        <PageHeading>{t('setup.layout.title')}</PageHeading>

        {/* Live, beside the canvas, as fields are added. */}
        <span className="flex flex-col">
          <Num data-field-counter className="text-body text-ink">
            {t('setup.layout.counter', { values: { count: formatCount(placed.length, format) } })}
          </Num>
          {overBudget > 0 ? (
            <Num data-over-budget className="text-meta text-warning-text">
              {t('setup.layout.overBudget', {
                values: {
                  budget: formatCount(data.data.budget, format),
                  over: formatCount(overBudget, format),
                },
              })}
            </Num>
          ) : null}
        </span>

        <Button
          intent="primary"
          data-save-layout
          disabled={save.isPending}
          onClick={() => save.mutate(placed)}
          className="ms-auto"
        >
          {t('action.save')}
        </Button>
      </header>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[var(--panel-narrow)_1fr]">
        <Panel data-region="available">
          <div className="flex flex-col gap-2">
            <SectionLabel>{t('setup.layout.available')}</SectionLabel>
            <ul className="flex flex-col">
              {available.map((field) => (
                <li key={field.key} className="border-t border-t-rule py-1 first:border-t-0">
                  <button
                    type="button"
                    data-add-field={field.key}
                    aria-label={t('setup.layout.addField', { values: { field: field.label } })}
                    onClick={() => setPlaced((current) => [...(current ?? []), field.key])}
                    className="flex w-full items-center gap-2 text-start text-dense text-ink hover:text-bronze-text"
                  >
                    <Icon name="Plus" size="inline" decorative />
                    <span className="min-w-0 flex-1 truncate">{field.label}</span>
                    {field.isCustom ? (
                      <span className="text-meta text-ink-meta">{t('setup.field.new')}</span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </Panel>

        <Panel data-region="canvas">
          <div className="flex flex-col gap-2">
            <SectionLabel>{t('setup.layout.canvas')}</SectionLabel>
            {placed.length === 0 ? (
              <p className="text-body text-ink-meta">{t('setup.layout.empty')}</p>
            ) : (
              <ol className="flex flex-col">
                {placed.map((key, index) => {
                  const field = data.data.fields.find((entry) => entry.key === key);
                  // Past the budget, the row says so in place rather than
                  // leaving the reader to count.
                  const beyond = index >= data.data.budget;
                  return (
                    <li
                      key={key}
                      data-placed-field={key}
                      data-beyond-budget={beyond || undefined}
                      className="flex items-center gap-2 border-t border-t-rule py-1 first:border-t-0"
                    >
                      <span className="min-w-0 flex-1 truncate text-dense text-ink">
                        {field?.label ?? key}
                      </span>
                      {beyond ? (
                        <span className="text-meta text-warning-text">
                          {t('record.mode.advanced')}
                        </span>
                      ) : null}
                      <button
                        type="button"
                        data-remove-field={key}
                        aria-label={t('setup.layout.removeField', {
                          values: { field: field?.label ?? key },
                        })}
                        onClick={() =>
                          setPlaced((current) => (current ?? []).filter((entry) => entry !== key))
                        }
                        className="text-ink-meta hover:text-danger-text"
                      >
                        <Icon name="X" size="inline" decorative />
                      </button>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function LayoutBuilderEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="GridFour"
      title={t('setup.layout.title')}
      body={t('setup.layout.empty')}
      primary={{ label: t('empty.action.addSection'), icon: 'Plus', href: '/setup' }}
    />
  );
}

function LayoutBuilderLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-block-md" />
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Skeleton className="h-block-xl w-full" />
        <Skeleton className="h-block-xl w-full" />
      </div>
    </div>
  );
}

function LayoutBuilderError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="The layout could not be loaded"
      body={error instanceof Error ? error.message : 'The builder did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-32',
  route: '/setup/objects/:obj/layout',
  surface: 'page',
  title: 'Layout builder',
  features: ['F-012'],
  entitlement: 'always-on',
  reachedBy: [{ kind: 'screen', from: 'SC-30', how: 'an object in the Setup list' }],
  components: [],
  elements: ['LayoutBuilder'],
  sampleParams: { obj: 'contact' },
  states: {
    empty: LayoutBuilderEmpty,
    loading: LayoutBuilderLoading,
    error: LayoutBuilderError,
  },
  component: LayoutBuilderScreen,
});

export default screen;
